import Calendar from '@toast-ui/calendar';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import { pageWidth } from '../functions/windowSizeFunctions';

export default class WCProductCalendar {
    constructor() {
        this.calendarInitialized = false;
        this.activeProductCalendar = null;
        this.coursesColor = '#0B65F2';
        this.elearningColor = '#1D6388';

        this.variationForm = document.querySelector('.jsVariableProductForm');
        this.calendarShowBtn = document.querySelector('.jsCalendarShowBtn');

        this.dateSelectorWrap = this.variationForm
            ? this.variationForm.querySelector('.jsDateSelectorWrap')
            : null;

        this.dateSelectorData =
            this.dateSelectorWrap &&
            this.dateSelectorWrap?.dataset?.dateSelector
                ? JSON.parse(this.dateSelectorWrap.dataset.dateSelector)
                : {};

        this.productListCalendar = document.querySelector('.jsTuiCalendar');
        this.productListCalendarNav =
            document.querySelector('.jsTuiCalendarNav');

        this.calendarNavNext = this.productListCalendarNav
            ? this.productListCalendarNav.querySelector('.jsTuiCalendarBtnNext')
            : null;
        this.calendarNavPrev = this.productListCalendarNav
            ? this.productListCalendarNav.querySelector('.jsTuiCalendarBtnPrev')
            : null;
        this.calendarNavToday = this.productListCalendarNav
            ? this.productListCalendarNav.querySelector(
                  '.jsTuiCalendarBtnToday',
              )
            : null;
        this.calendarRange = this.productListCalendarNav
            ? this.productListCalendarNav.querySelector('.jsTuiCalendarRange')
            : null;

        if (
            this.calendarShowBtn &&
            this.productListCalendar &&
            Object.keys(this.dateSelectorData)?.length
        )
            this.events();
    }

    events() {
        this.calendarShowBtn.addEventListener(
            'click',
            this.initProductCalendar.bind(this),
            { once: true },
        );

        if (this.calendarNavNext)
            this.calendarNavNext.addEventListener('click', (e) => {
                if (this.calendarInitialized && this.activeProductCalendar) {
                    this.activeProductCalendar.next();
                    this.displayRenderRange();
                }
            });
        if (this.calendarNavPrev)
            this.calendarNavPrev.addEventListener('click', (e) => {
                if (this.calendarInitialized && this.activeProductCalendar) {
                    this.activeProductCalendar.prev();
                    this.displayRenderRange();
                }
            });

        if (this.calendarNavToday)
            this.calendarNavToday.addEventListener('click', (e) => {
                if (this.calendarInitialized && this.activeProductCalendar) {
                    this.activeProductCalendar.today();
                    this.displayRenderRange();
                }
            });
    }

    initProductCalendar() {
        if (this.calendarInitialized) return;

        const currentPageWidth = pageWidth();

        // eslint-disable-next-line no-unused-vars
        this.activeProductCalendar = new Calendar(this.productListCalendar, {
            defaultView: 'month',
            isReadOnly: true,
            usageStatistics: false,
            timezone: {
                zones: [
                    {
                        timezoneName: 'Europe/Warsaw',
                        displayLabel: 'Warsaw',
                    },
                ],
            },
            month: {
                startDayOfWeek: 1,
                visibleEventCount: 4,
                workweek: false,
                dayNames: [
                    'Niedz.',
                    'Pon.',
                    'Wt.',
                    'Śr.',
                    'Czw.',
                    'Pt.',
                    'Sob.',
                ],
            },
            template: {
                allday(event) {
                    return `<span style="font-size:0.625rem;">${event.title}</span>`;
                },
                monthMoreTitleDate(moreTitle) {
                    const { ymd } = moreTitle;

                    const displayDate = dayjs(ymd)
                        .locale('pl')
                        .format('DD MMMM');

                    const displayDay = dayjs(ymd).locale('pl').format('(dddd)');

                    return `<span class="toastui-calendar-more-title-date">${displayDate}</span>
                    <span class="toastui-calendar-more-title-day">${displayDay}</span>`;
                },
                monthGridHeaderExceed(hiddenEvents) {
                    const text = 'więcej';

                    return `<span class="toastui-calendar-weekday-grid-more-events toastui-calendar-template-monthGridHeaderExceed">${hiddenEvents} ${text}</span>`;
                },
            },
            theme: {
                month: {
                    moreView: {
                        height: currentPageWidth < 768 ? 200 : 260,
                        width: currentPageWidth < 768 ? 280 : 320,
                    },
                },
            },
        });

        this.calendarInitialized = true;

        // eslint-disable-next-line new-cap
        this.activeProductCalendar.setDate(new dayjs().format('YYYY-MM-DD'));
        this.displayRenderRange();

        this.addCalendars();
        this.addEvents();

        this.activeProductCalendar.on('clickEvent', ({ event }) => {
            this.productListCalendar.dispatchEvent(
                new CustomEvent('updateVariationFromCalendar', {
                    detail: event.raw,
                }),
            );
        });
    }

    addCalendars() {
        if (!this.calendarInitialized || !this.activeProductCalendar) return;

        const calendars = [];

        for (const item in this.dateSelectorData) {
            if (this.dateSelectorData[item].length) {
                const firstElem = this.dateSelectorData[item][0];

                const calendar = {
                    id: item,
                    name: firstElem.location_name,
                    color: '#fff',
                    backgroundColor:
                        firstElem.location_icon === 'onsite'
                            ? this.coursesColor
                            : this.elearningColor,
                    dragBackgroundColor:
                        firstElem.location_icon === 'onsite'
                            ? this.coursesColor
                            : this.elearningColor,
                };

                calendars.push(calendar);
            }
        }

        this.activeProductCalendar.setCalendars(calendars);
    }

    addEvents() {
        if (!this.calendarInitialized || !this.activeProductCalendar) return;

        const calendarEvents = [];

        const currentPageWidth = pageWidth();

        for (const item in this.dateSelectorData) {
            if (this.dateSelectorData[item].length) {
                this.dateSelectorData[item].forEach((evItem) => {
                    const event = {
                        id: evItem.slugified_name,
                        calendarId: item,
                        title:
                            currentPageWidth < 768
                                ? evItem.location_name
                                : `${evItem.name} - ${evItem.location_name}`,
                        body: '',
                        category: 'allday',
                        start: dayjs(evItem.start_date, 'Ymd'),
                        end: dayjs(evItem.end_date, 'Ymd'),
                        isReadOnly: true,
                        isVisible: true,
                        raw: {
                            variation_id: item,
                            course_slugified_name: evItem.slugified_name,
                            course_term_name: evItem.term_slug,
                        },
                    };

                    calendarEvents.push(event);
                });
            }
        }

        this.activeProductCalendar.createEvents(calendarEvents);
    }

    displayRenderRange() {
        if (!this.calendarRange) return;

        const rangeStart = this.activeProductCalendar.getDateRangeStart();
        const rangeEnd = this.activeProductCalendar.getDateRangeEnd();

        const start = rangeStart.toDate();
        const end = rangeEnd.toDate();
        const middle = new Date(
            start.getTime() + (end.getTime() - start.getTime()) / 2,
        );

        const rangeToDisplay = dayjs(middle).locale('pl').format('MMMM YYYY');

        this.calendarRange.textContent = rangeToDisplay;
    }
}
